"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function ResidentialHero() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, ctx => {
      const {
        reduced
      } = ctx.conditions as {
        reduced: boolean;
      };
      if (reduced) {
        gsap.set([".hero-eyebrow", ".hero-headline", ".hero-subhead", ".hero-ctas"], {
          opacity: 1,
          y: 0
        });
        return;
      }
      gsap.from(".hero-eyebrow", {
        y: 12,
        opacity: 0,
        duration: 0.38,
        ease: "power2.out"
      });
      gsap.from(".hero-headline", {
        y: 16,
        opacity: 0,
        duration: 0.48,
        delay: 0.09,
        ease: "power2.out"
      });
      gsap.from(".hero-subhead", {
        y: 12,
        opacity: 0,
        duration: 0.42,
        delay: 0.18,
        ease: "power2.out"
      });
      gsap.from(".hero-ctas", {
        y: 12,
        opacity: 0,
        duration: 0.42,
        delay: 0.26,
        ease: "power2.out"
      });
      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      if (isDesktop) {
        gsap.to(".hero-image", {
          yPercent: 8,
          ease: "none",
          scrollTrigger: {
            trigger: container.current,
            start: "top top",
            end: "bottom top",
            scrub: true
          }
        });
      }
    });
  }, {
    scope: container
  });
  return <section data-section-id="32706" id="residentialhero" ref={container} className="bg-background text-foreground relative min-h-screen w-full overflow-hidden pb-[clamp(80px,10vw,140px)]">
      {}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/summit_mountain_clean.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/summit_mountain_clean.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/summit_mountain_clean.png-wvc-sizes" alt="Crisp mountain peak representing professional cleaning standards" className="hero-image absolute inset-0 h-[115%] w-full object-cover" />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to top, hsl(220 40% 15% / 0.85), hsl(220 40% 15% / 0.45) 55%, hsl(220 40% 15% / 0.25))"
      }} />
      </div>

      {}
      <div className="relative z-10 flex min-h-screen flex-col justify-end px-[clamp(20px,4vw,72px)] pt-32">
        <div className="mx-auto w-full max-w-[1440px]">
          <div className="w-full md:w-8/12">
            <div className="hero-eyebrow mb-8 flex items-center">
              <span className="inline-block h-3 w-px" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
              <span className="mx-3 text-[clamp(0.75rem,0.85vw,0.8125rem)] font-semibold uppercase leading-none text-primary-foreground" style={{
              letterSpacing: "0.22em"
            }}>
                Professional Cleaning Services
              </span>
              <span className="inline-block h-3 w-px" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
            </div>

            <h1 className="hero-headline font-serif text-primary-foreground" style={{
            fontSize: "clamp(3.75rem, 10vw, 9.5rem)",
            fontWeight: 400,
            letterSpacing: "0.005em",
            lineHeight: 0.92,
            textTransform: "uppercase"
          }}>
              <span className="block">Residential</span>
              <span className="block md:pl-[8.333%]">Excellence.</span>
            </h1>

            <p className="hero-subhead mt-8 max-w-[52ch] text-primary-foreground/85" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: 1.6
          }}>
              Immaculate residential cleaning tailored to your home across Northeast Ohio. 
              Our vetted network of professional cleaners delivers reliable, pristine results every time.
            </p>

            <div className="hero-ctas mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
              <Button asChild className="h-[52px] rounded-md px-7 text-sm font-semibold uppercase shadow-md bg-primary text-primary-foreground hover:bg-primary/90" style={{
              letterSpacing: "0.04em"
            }}>
                <Link to="/GetAQuotePage" className="group inline-flex items-center gap-2 hover:bg-transparent">
                  <span>Get a Quote</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>

              <Link to="/ViewServicesPage" className="group relative inline-flex h-[52px] items-center px-1 text-sm font-semibold uppercase text-primary-foreground" style={{
              letterSpacing: "0.04em"
            }}>
                <span className="relative">
                  View Our Services
                  <span aria-hidden="true" className="absolute -bottom-1 left-0 h-px w-full transition-all duration-200 group-hover:opacity-100" style={{
                  backgroundColor: "var(--primary-foreground)",
                  opacity: 0.4
                }} />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}